// UGH!3D shared level definition — runs in both Node (server) and browser (client).
// Everything here is deterministic and analytic so server and client agree on the
// world without exchanging geometry: terrain height, cave platforms, fruit spots.

export const LEVEL_V = 1;

export const WORLD_RADIUS = 560;   // playable valley radius (soft wall beyond)
// The soft headwind wall. It has to sit INSIDE the point (~r 523) where the
// rim wall grows through the analytic ceiling — otherwise the rim is a ramp
// you can ride up and over the sealed roof.
export const WALL_RADIUS = 470;
export const WATER_Y = 0;          // water plane height; terrain below this is river/lake

// ---------------------------------------------------------------- terrain
// Rolling jungle floor + raised rim ringing the valley + a winding river.
export function riverX(z) {
  return 90 * Math.sin(z * 0.004) + 30 * Math.sin(z * 0.011 + 2.1);
}

function smoothstep(t) {
  t = Math.max(0, Math.min(1, t));
  return t * t * (3 - 2 * t);
}

export function terrainHeight(x, z) {
  const hills =
    6 * Math.sin(x * 0.008) * Math.cos(z * 0.008) +
    3.2 * Math.sin(x * 0.021 + 1.7) * Math.sin(z * 0.017 + 0.6) +
    1.4 * Math.sin(x * 0.05 + 4.0) * Math.cos(z * 0.043 + 1.2) +
    12.5;
  const r = Math.hypot(x, z);
  const rim = smoothstep((r - 430) / 170) * 70;
  const dRiver = Math.abs(x - riverX(z));
  const river = 16 * Math.exp(-(dRiver * dRiver) / (34 * 34));
  return hills + rim - river;
}

// ---------------------------------------------------------------- caves
// Numbered cave platforms on cliff towers around the valley. `towerH` is the
// platform height above local terrain; `face` (radians) is the direction the
// cave mouth looks (roughly toward the valley center). PAD_R is the landing
// zone radius around the platform center.
export const PAD_R = 9;

// Tower heights keep every pad UNDER the giant-tree canopy — the taxi routes
// live in the dim layer between jungle floor and the leaf ceiling.
const CAVE_DEFS = [
  { id: 1, x:  150, z:  240, towerH: 14 },
  { id: 2, x: -260, z:  180, towerH: 21 },
  { id: 3, x: -330, z: -160, towerH: 16 },
  { id: 4, x:  -60, z: -340, towerH: 20 },
  { id: 5, x:  260, z: -250, towerH: 18 },
  { id: 6, x:  380, z:   60, towerH: 22 },
  { id: 7, x:  -80, z:  390, towerH: 19 },
  { id: 8, x: -420, z:   30, towerH: 20 },
];

export const CAVES = CAVE_DEFS.map(c => ({
  ...c,
  y: terrainHeight(c.x, c.z) + c.towerH,     // platform walking surface height
  face: Math.atan2(-c.x, -c.z),              // yaw so cave mouth faces origin
}));

// Home pad: Cavelon's own cave / spawn + repair pad, near the middle.
export const HOME = { x: 20, z: 40, y: terrainHeight(20, 40) + 6 };

// ---------------------------------------------------------------- fruit
// Energy pickups hovering above the canopy. Types cycle deterministically.
export const FRUIT_TYPES = ['banana', 'melon', 'meat'];

const FRUIT_DEFS = [
  [60, 150], [-140, 60], [-40, -180], [180, -60], [300, -140],
  [-240, -60], [-180, 280], [80, 320], [240, 140], [-320, 120],
  [-160, -280], [40, -300], [340, -40], [-60, 220],
];

export const FRUIT_SPOTS = FRUIT_DEFS.map(([x, z], i) => ({
  id: i,
  x, z,
  y: terrainHeight(x, z) + 9 + (i % 3) * 3,
  type: FRUIT_TYPES[i % FRUIT_TYPES.length],
}));

// ---------------------------------------------------------------- pteros
// Patrol circles for the pterodactyls: center, radius, altitude, angular speed.
// The roof is closed, so the pterodactyls glide INSIDE the hall with you,
// weaving through the mid-air layer where the taxi routes run.
export const PTEROS = [
  { id: 0, cx:  120, cz: -60, r: 190, alt: 28, w:  0.14, phase: 0 },
  { id: 1, cx: -160, cz: 140, r: 230, alt: 36, w: -0.11, phase: 2.4 },
];

// ---------------------------------------------------------------- t-rexes
// Sleeping tyrannosaurs on the jungle floor along busy flight lanes. Fly close
// with a noisy rotor and they wake, then blow you away with a roar.
// WAKE_R: horizontal wake radius; BLAST_R: roar blast reach.
export const REX_WAKE_R = 45;
export const REX_BLAST_R = 80;

const REX_DEFS = [
  { id: 0, x: 70, z: 130 },
  { id: 1, x: -190, z: 60 },
  { id: 2, x: 140, z: -170 },
  { id: 3, x: -150, z: -200 },
];

export const REXES = REX_DEFS.map(r => ({
  ...r,
  y: terrainHeight(r.x, r.z),
  face: Math.atan2(-r.x, -r.z) + (r.id % 2 ? 1.1 : -0.8), // roughly toward the valley, varied
}));

// ---------------------------------------------------------------- stones
// Throwable stones (the classic Ugh! defense): piles on open ground, grab one
// by flying through, throw with F. A hit stuns a rex or downs a ptero.
export const REX_STUN_MS = 20_000;
export const PTERO_STUN_MS = 15_000;

const STONE_DEFS = [
  [-30, 110], [-120, -40], [90, -90], [-90, 170], [200, -30], [-230, -140],
];

export const STONE_SPOTS = STONE_DEFS.map(([x, z], i) => ({
  id: i,
  x, z,
  y: terrainHeight(x, z),
}));

// ---------------------------------------------------------------- weather
// Server-owned shared weather: clear ⇄ rain. During rain the pedals are wet
// (less lift, faster energy drain) and lightning strikes with a short warning.
export const BOLT_WARN_MS = 950;   // glowing ground circle before the strike
export const BOLT_RADIUS = 14;     // blast radius of a strike
export const BOLT_DAMAGE = 25;

// ---------------------------------------------------------------- economy
export const FARE_PER_METER = 0.09;   // coins per meter of trip distance
export const FARE_BASE = 10;
export const PATIENCE_MS = 75_000;    // passenger gives up after this

export function fareFor(fromCave, toCave) {
  const d = Math.hypot(fromCave.x - toCave.x, fromCave.z - toCave.z);
  return Math.round(FARE_BASE + d * FARE_PER_METER);
}

const c01 = v => Math.max(0, Math.min(1, v));

// Landing quality 0..1 from touchdown speeds — the SINGLE source of truth for
// the HUD verdict, the landing sound and the server's tip. 1 = feather, 0 = slam.
export function landScore(vy, vh) {
  return c01(1 - (vy - 1.2) / 3.6) * c01(1 - (vh - 2.0) / 8.0);
}

// Whole-ride quality: how you set down, how much you got beaten up on the way
// and whether you took your time. Feeds tipFor().
export function rideQuality({ vy, vh, dmgTaken, elapsedMs, dist }) {
  const calm = c01(1 - dmgTaken / 28);
  const par = 7000 + (dist / 17) * 1000;
  const fast = c01(1.30 - elapsedMs / par);
  return 0.45 * landScore(vy, vh) + 0.30 * calm + 0.25 * fast;
}

// Tip on top of the fare: a sloppy ride still gets a token 4 %, a perfect one 50 %.
export function tipFor(fare, q) { return Math.round(fare * (0.04 + 0.46 * q)); }

// ---------------------------------------------------------------- canopy
// Giant jungle trees whose crowns form a broken leaf-ceiling over the valley.
// The taxi routes live UNDER it: trunks are hard obstacles, crown foliage
// drags you down, lianas tangle the rotor — and above the canopy wait the
// pterodactyls and the lightning. Deterministic (seeded) so every client and
// the server agree without exchanging geometry.
export const CANOPY_ABS = 55;   // kept for compat; the roof is now CLOSED

// The leaf roof is a hard, closed ceiling — the whole game is played inside
// the jungle hall between floor and roof. Undulates so there are low passages.
export function ceilingY(x, z) {
  return 55 + 4.5 * Math.sin(x * 0.011 + 1) + 4 * Math.cos(z * 0.013) + 2 * Math.sin((x + z) * 0.02);
}

function mulberry(seed) {
  let a = seed >>> 0;
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function clearOfPOIs(x, z, caveD, homeD, rexD, stoneD, fruitD) {
  if (Math.hypot(x - HOME.x, z - HOME.z) < homeD) return false;
  for (const c of CAVES) if (Math.hypot(x - c.x, z - c.z) < caveD) return false;
  for (const r of REXES) if (Math.hypot(x - r.x, z - r.z) < rexD) return false;
  for (const s of STONE_SPOTS) if (Math.hypot(x - s.x, z - s.z) < stoneD) return false;
  for (const f of FRUIT_SPOTS) if (Math.hypot(x - f.x, z - f.z) < fruitD) return false;
  return true;
}

// Floor-to-roof pillar trees: trunks now REACH the ceiling, so the jungle
// reads as a great hall of columns you have to thread between.
export const GIANT_TREES = (() => {
  const rand = mulberry(0xC4A0F1);
  const trees = [];
  let guard = 0;
  while (trees.length < 200 && guard++ < 12000) {
    const a = rand() * Math.PI * 2, rr = Math.sqrt(rand()) * 520;
    const x = Math.cos(a) * rr, z = Math.sin(a) * rr;
    const ty = terrainHeight(x, z);
    if (ty < WATER_Y + 1 || ty > 40) continue;
    if (Math.abs(x - riverX(z)) < 22) continue;
    if (!clearOfPOIs(x, z, 34, 36, 28, 12, 10)) continue;
    if (trees.some(t => Math.hypot(t.x - x, t.z - z) < 19)) continue;
    const h = Math.max(20, ceilingY(x, z) - ty - 4); // crown center just under the roof
    trees.push({
      x: Math.round(x), z: Math.round(z),
      baseY: ty,
      h,
      r: 22 + rand() * 14,          // crown radius (melts into the roof)
      trunkR: 2.4 + rand() * 1.8,
      kind: rand() < 0.55 ? 'palm' : 'oak',
      seed: Math.floor(rand() * 1e6),
    });
  }
  return trees;
})();

// Hanging obstacles: lianas from tree crowns PLUS ropes straight from the
// roof itself — the air is full of things to dodge.
export const LIANAS = (() => {
  const rand = mulberry(0x11A9A5);
  const lianas = [];
  const push = (x, z, topY, lenWanted) => {
    const maxLen = topY - (terrainHeight(x, z) + 5);
    if (maxLen < 8) return;
    lianas.push({
      x: Math.round(x * 10) / 10, z: Math.round(z * 10) / 10,
      topY: Math.round(topY * 10) / 10,
      len: Math.round(Math.min(lenWanted, maxLen) * 10) / 10,
      phase: rand() * Math.PI * 2,
    });
  };
  for (const t of GIANT_TREES) {
    const n = 1 + (rand() < 0.55 ? 1 : 0) + (rand() < 0.2 ? 1 : 0);
    for (let i = 0; i < n; i++) {
      const a = rand() * Math.PI * 2, d = 5 + rand() * t.r * 0.6;
      push(t.x + Math.cos(a) * d, t.z + Math.sin(a) * d, t.baseY + t.h - 4, 14 + rand() * 12);
    }
  }
  let guard = 0, roofN = 0;
  while (roofN < 170 && guard++ < 4000) {
    const a = rand() * Math.PI * 2, rr = Math.sqrt(rand()) * 500;
    const x = Math.cos(a) * rr, z = Math.sin(a) * rr;
    if (!clearOfPOIs(x, z, 26, 28, 20, 8, 8)) continue;
    push(x, z, ceilingY(x, z) - 1, 12 + rand() * 16);
    roofN++;
  }
  return lianas;
})();

// ---------------------------------------------------------------- progression
// The tribe levels up together: every completed fare counts toward the shared
// quota; each new bane makes the jungle meaner but the pay better.
export function quotaFor(n) { return Math.min(2 + n, 10); }
export function patienceFor(n) { return Math.max(40_000, 78_000 - 3_000 * n); }
// Capped on purpose: fareMult, streakMult and the tip all multiply, so an
// uncapped fare multiplier turns bane 20+ into a coin fountain.
export function fareMultFor(n) { return 1 + 0.08 * Math.min(n - 1, 12); }
// Capped like fareMult: the max trip payout tops out around 264, so an
// uncapped clear bonus just inflates the wallet forever.
export function bonusFor(n) { return Math.min(300, 20 + 10 * n); }   // paid when bane n is cleared
export function rexWakeRFor(n) { return Math.min(70, 44 + 2 * n); }

// --- t-rex reach. Blast grows faster than the wake radius so a woken rex is
// always worth fleeing, and the roar repeats while you linger in range.
export function rexBlastRFor(n) { return Math.min(130, 80 + 4 * (n - 1)); }
export function rexRoarPeriodFor(n) { return Math.max(3200, 6000 - 250 * (n - 1)); }

// --- lightning. Height above ground decides how hard a bolt bites; the crown
// foliage is a lightning shelter and a pad is completely safe.
export function boltFactorFor(agl, landedOnPad, inCrown) {
  if (landedOnPad) return 0;
  let f = agl < 12 ? 0 : agl < 30 ? 0.45 : agl < 42 ? 1.0 : 1.5;
  return inCrown ? f * 0.5 : f;
}

// --- customer patience. Scaled by how far the nearest taxi actually is, so a
// far-away fare is never geometrically impossible — only tighter at high banes.
export function patienceScale(n) { return Math.max(0.62, 1 - 0.035 * (n - 1)); }
export function patienceForPickup(n, dPickup) {
  const travelMs = (dPickup / 18) * 1000;
  return Math.round(Math.max(26_000, Math.max(32_000, travelMs * 2.0 + 16_000) * patienceScale(n)));
}

// --- effort. A delivery streak pays more; a wreck costs a repair fee.
export function streakMult(s) { return 1 + 0.12 * Math.min(5, Math.max(0, s - 1)); }
export function repairFeeFor(n) { return Math.min(60, 8 + 3 * n); }

// --- pterodactyl dives. The server announces a dive DIVE_LEAD_MS ahead of the
// start so every client can play the wind-up in sync; the client's stale-message
// guard has to allow for exactly that lead, or the dive is thrown away on the
// first frame after it arrives.
export const DIVE_LEAD_MS = 1500;

// --- pterodactyl dive cooldown per bird.
export function pteroDiveCdFor(n) { return Math.max(9000, 26000 - 1200 * (n - 1)); }

// More pterodactyls take wing as the tribe climbs the banes (ids stay stable
// so stun messages keep working across levels).
export function pterosForLevel(n) {
  const extra = Math.min(4, Math.floor((n - 1) / 2));
  const list = [...PTEROS];
  for (let i = 0; i < extra; i++) {
    const id = 2 + i;
    list.push({
      id,
      cx: Math.round(250 * Math.sin(id * 2.1)),
      cz: Math.round(250 * Math.cos(id * 1.3)),
      r: 150 + 45 * i,
      alt: 24 + 6 * i,
      w: (i % 2 ? -1 : 1) * (0.13 + 0.02 * i),
      phase: id * 1.7,
    });
  }
  return list;
}

// ---------------------------------------------------------------- selftest
const isNodeMain = typeof process !== 'undefined' && process.argv?.[1]?.endsWith('level.mjs');
if (isNodeMain) {
  let ok = true;
  for (const c of CAVES) {
    const t = terrainHeight(c.x, c.z);
    if (!(c.y > t + 10)) { console.error(`cave ${c.id} too low`, c.y, t); ok = false; }
    for (const o of CAVES) {
      if (o.id !== c.id && Math.hypot(o.x - c.x, o.z - c.z) < 120) {
        console.error(`caves ${c.id}/${o.id} too close`); ok = false;
      }
    }
    if (Math.hypot(c.x, c.z) > WORLD_RADIUS) { console.error(`cave ${c.id} outside world`); ok = false; }
  }
  for (const f of FRUIT_SPOTS) {
    if (!(f.y > terrainHeight(f.x, f.z))) { console.error(`fruit ${f.id} underground`); ok = false; }
  }
  for (const r of REXES) {
    if (!(r.y > WATER_Y + 1)) { console.error(`rex ${r.id} in the river`, r.y); ok = false; }
    for (const c of CAVES) if (Math.hypot(c.x - r.x, c.z - r.z) < 60) { console.error(`rex ${r.id} too close to cave ${c.id}`); ok = false; }
    if (Math.hypot(HOME.x - r.x, HOME.z - r.z) < 60) { console.error(`rex ${r.id} too close to home`); ok = false; }
    for (const f of FRUIT_SPOTS) if (Math.hypot(f.x - r.x, f.z - r.z) < 18) { console.error(`rex ${r.id} on fruit ${f.id}`); ok = false; }
  }
  for (const s of STONE_SPOTS) {
    if (!(s.y > WATER_Y + 0.5)) { console.error(`stone ${s.id} in water`, s.y); ok = false; }
    if (Math.hypot(HOME.x - s.x, HOME.z - s.z) < 25) { console.error(`stone ${s.id} too close to home`); ok = false; }
    for (const c of CAVES) if (Math.hypot(c.x - s.x, c.z - s.z) < 30) { console.error(`stone ${s.id} too close to cave ${c.id}`); ok = false; }
    for (const f of FRUIT_SPOTS) if (Math.hypot(f.x - s.x, f.z - s.z) < 12) { console.error(`stone ${s.id} on fruit ${f.id}`); ok = false; }
    for (const r of REXES) if (Math.hypot(r.x - s.x, r.z - s.z) < 35) { console.error(`stone ${s.id} too close to rex ${r.id}`); ok = false; }
  }
  const fares = [];
  for (const a of CAVES) for (const b of CAVES) if (a.id < b.id) fares.push(fareFor(a, b));
  console.log(`caves=${CAVES.length} fruit=${FRUIT_SPOTS.length} fare min/max = ${Math.min(...fares)}/${Math.max(...fares)}`);
  for (const n of [1, 5, 10, 20, 69]) {
    const pt = pterosForLevel(n);
    if (new Set(pt.map(p => p.id)).size !== pt.length) { console.error(`level ${n}: dublet-ptero-ids`); ok = false; }
    if (!(quotaFor(n) >= 3 && quotaFor(n) <= 10)) { console.error(`level ${n}: quota ude af interval`); ok = false; }
    if (!(patienceFor(n) >= 40_000)) { console.error(`level ${n}: patience under gulv`); ok = false; }
  }
  console.log(`progression: L1 quota=${quotaFor(1)} pteros=${pterosForLevel(1).length} → L10 quota=${quotaFor(10)} pteros=${pterosForLevel(10).length} fareMult=${fareMultFor(10).toFixed(2)}`);
  // closed-roof sanity: every pad and fruit has headroom, nothing drowned
  for (const t of GIANT_TREES) {
    if (t.baseY < WATER_Y) { console.error('giant tree in water'); ok = false; }
  }
  for (const c of CAVES) {
    if (GIANT_TREES.find(t => Math.hypot(t.x - c.x, t.z - c.z) < 34)) { console.error(`giant tree crowds cave ${c.id}`); ok = false; }
    if (!(ceilingY(c.x, c.z) > c.y + 9)) { console.error(`cave ${c.id} pad too close to roof: pad ${c.y.toFixed(1)} vs ceil ${ceilingY(c.x, c.z).toFixed(1)}`); ok = false; }
  }
  if (!(ceilingY(HOME.x, HOME.z) > HOME.y + 9)) { console.error('home pad too close to roof'); ok = false; }
  for (const f of FRUIT_SPOTS) {
    if (!(ceilingY(f.x, f.z) > f.y + 4)) { console.error(`fruit ${f.id} in the roof`); ok = false; }
  }
  for (const l of LIANAS) {
    if (l.topY - l.len < terrainHeight(l.x, l.z) + 2) { console.error('liana touches ground'); ok = false; }
  }
  let minGap = 1e9;
  // only inside the soft wall — beyond it the rim wall legitimately grows
  // through the ceiling, and the player is pushed back before he gets there
  for (let sx = -WALL_RADIUS; sx <= WALL_RADIUS; sx += 10) for (let sz = -WALL_RADIUS; sz <= WALL_RADIUS; sz += 10) {
    if (Math.hypot(sx, sz) > WALL_RADIUS) continue;
    minGap = Math.min(minGap, ceilingY(sx, sz) - terrainHeight(sx, sz));
  }
  if (minGap < 6) { console.error(`ceiling dips to ${minGap.toFixed(1)} m over the terrain inside the wall`); ok = false; }
  console.log(`lukket hal: ${GIANT_TREES.length} søjletræer, ${LIANAS.length} hængende, loft [~${ceilingY(-300, 0).toFixed(0)}-${ceilingY(300, 200).toFixed(0)}], min loftshøjde over terræn i dalen ≈ ${minGap.toFixed(1)} m`);
  // --- economy / threat curves
  const approx = (a, b, eps = 1e-9) => Math.abs(a - b) < eps;
  if (!approx(landScore(0.8, 1), 1)) { console.error('landScore(0.8,1) != 1'); ok = false; }
  if (!approx(landScore(6, 12), 0)) { console.error('landScore(6,12) != 0'); ok = false; }
  const qGood = rideQuality({ vy: 0.8, vh: 1, dmgTaken: 0, elapsedMs: 20000, dist: 400 });
  const qBad = rideQuality({ vy: 6, vh: 12, dmgTaken: 40, elapsedMs: 120000, dist: 400 });
  if (!(qGood > 0.85)) { console.error('perfekt tur for lav kvalitet', qGood); ok = false; }
  if (!(qBad < 0.10)) { console.error('slasket tur for høj kvalitet', qBad); ok = false; }
  console.log(`tur: slasket +${tipFor(100, qBad)} %, gennemsnit +${tipFor(100, rideQuality({ vy: 3.0, vh: 5.0, dmgTaken: 14, elapsedMs: 40000, dist: 400 }))} %, perfekt +${tipFor(100, qGood)} % drikkepenge`);
  // rex danger must never get *softer* as the banes climb
  let prevDanger = -Infinity;
  for (let n = 1; n <= 30; n++) {
    const danger = 10 + 22 * (1 - rexWakeRFor(n) / rexBlastRFor(n));
    if (danger < prevDanger - 1e-9) { console.error(`rex-fare falder på bane ${n}`); ok = false; }
    prevDanger = danger;
    if (!(rexRoarPeriodFor(n) >= 3200)) { console.error(`brøl-periode under gulv på bane ${n}`); ok = false; }
  }
  // lightning factor: never decreasing with height above ground
  let prevF = -1;
  for (let agl = 0; agl <= 80; agl += 1) {
    const f = boltFactorFor(agl, false, false);
    if (f < prevF - 1e-9) { console.error(`boltFactorFor falder ved agl ${agl}`); ok = false; }
    prevF = f;
    if (boltFactorFor(agl, true, false) !== 0) { console.error('lyn rammer en parkeret heli'); ok = false; }
    if (boltFactorFor(agl, false, true) > f + 1e-9) { console.error('kronely gør lynet værre'); ok = false; }
  }
  // patience must cover the trip at every bane
  if (!(patienceForPickup(13, 801) > 55_000)) { console.error('L13 lang tur umulig', patienceForPickup(13, 801)); ok = false; }
  if (!(patienceForPickup(1, 210) >= 32_000)) { console.error('L1 kort tur for kort', patienceForPickup(1, 210)); ok = false; }
  console.log(`tålmodighed: L1 210/566/801 m = ${(patienceForPickup(1, 210) / 1000).toFixed(0)}/${(patienceForPickup(1, 566) / 1000).toFixed(0)}/${(patienceForPickup(1, 801) / 1000).toFixed(0)} s → L13 ${(patienceForPickup(13, 210) / 1000).toFixed(0)}/${(patienceForPickup(13, 801) / 1000).toFixed(0)} s`);
  if (!(streakMult(1) === 1 && streakMult(6) === streakMult(20))) { console.error('streakMult loft'); ok = false; }
  if (!(fareMultFor(30) === fareMultFor(13))) { console.error('fareMult mangler loft'); ok = false; }
  console.log(`indsats: stribe ×${streakMult(6).toFixed(2)} ved 6 i træk, reparation på bane 4 koster ${repairFeeFor(4)}`);
  console.log(ok ? 'LEVEL OK' : 'LEVEL FAIL');
  if (!ok) process.exit(1);
}
